import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Send, Phone, Camera } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { wsClient } from "@/lib/websocket";
import { MessageBubble } from "./MessageBubble";
import { VoiceNoteRecorder } from "./VoiceNoteRecorder";
import { QuickReplies } from "./QuickReplies";
import { TypingIndicator } from "./TypingIndicator";
import { VoiceCallOverlay } from "./VoiceCallOverlay";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useWebRTC } from "@/hooks/useWebRTC";
import { AnimatePresence } from "framer-motion";
import type { Companion } from "@shared/schema";

function MediaUploadButton({ onSend }: { onSend: (url: string, mode: "disappearing" | "static", mediaType: "image" | "video") => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ file: File; url: string; isVideo: boolean } | null>(null);
  const [mode, setMode] = useState<"disappearing" | "static">("disappearing");
  const [uploading, setUploading] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview({ file, url: URL.createObjectURL(file), isVideo: file.type.startsWith("video/") });
  };

  const handleSend = async () => {
    if (!preview || uploading) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", preview.file);
      const res = await fetch("/api/upload", { method: "POST", body: form });
      if (res.ok) {
        const { url } = await res.json();
        onSend(url, mode, preview.isVideo ? "video" : "image");
      }
    } finally {
      setUploading(false);
      URL.revokeObjectURL(preview.url);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const cancel = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <>
      <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm" onChange={handleFile} className="hidden" aria-label="Upload media" />
      <button type="button" onClick={() => fileRef.current?.click()} className="p-2 text-gray-400 hover:text-[#D4AF37] transition-colors" title="Send image or video">
        <Camera size={18} />
      </button>
      {preview && (
        <div className="fixed bottom-24 left-3 right-3 sm:absolute sm:bottom-16 sm:left-3 sm:right-3 bg-[#141414] border border-gray-800 rounded-xl p-3 z-[200] shadow-2xl">
          {preview.isVideo ? (
            <video src={preview.url} className="w-full max-h-40 object-cover rounded-lg mb-2" controls />
          ) : (
            <img src={preview.url} className="w-full max-h-40 object-cover rounded-lg mb-2" alt="Preview" />
          )}
          <div className="flex items-center justify-between gap-2">
            <button type="button" onClick={() => setMode(m => m === "disappearing" ? "static" : "disappearing")}
              className={`text-[9px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                mode === "disappearing" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-gray-700 text-gray-300"
              }`}
            >
              {mode === "disappearing" ? "One-time view" : "Static"}
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={cancel} className="text-xs text-gray-500 hover:text-white px-2 py-1">Cancel</button>
              <button type="button" onClick={handleSend} disabled={uploading}
                className="text-xs bg-[#D4AF37] text-black px-4 py-1.5 rounded-lg font-bold disabled:opacity-50">
                {uploading ? "..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export interface ConversationViewHandle {
  startCall: (convId?: string) => void;
  answerCall: (offer: RTCSessionDescriptionInit, conversationId?: string) => void;
}

interface ConversationViewProps {
  role: "customer" | "admin";
  userId: string;
  conversationId?: string;
  onConversationCreated?: (id: string) => void;
  showHeader?: boolean;
  headerTitle?: string;
  draftMessage?: string;
  onDraftConsumed?: () => void;
}

export const ConversationView = forwardRef<ConversationViewHandle, ConversationViewProps>(function ConversationView({
  role,
  userId,
  conversationId,
  onConversationCreated,
  draftMessage,
  onDraftConsumed,
  showHeader = true,
  headerTitle = "Live Support",
}, ref) {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    isConnected,
    messages,
    setMessages,
    isTyping,
    agentAvailable,
    sendMessage,
    sendTyping,
    markRead,
  } = useWebSocket({ role, userId, conversationId });

  const webrtc = useWebRTC();

  const { data: companions = [] } = useQuery<Companion[]>({
    queryKey: ["/api/companions"],
    enabled: role === "admin",
  });

  useImperativeHandle(ref, () => ({
    startCall: () => webrtc.startCall(conversationId),
    answerCall: (offer: RTCSessionDescriptionInit, convId?: string) =>
      webrtc.answerCall(offer, convId || conversationId),
  }), [webrtc, conversationId]);

  // Populate input with draft message from booking CTA
  useEffect(() => {
    if (draftMessage) {
      setInputValue(draftMessage);
      inputRef.current?.focus();
      onDraftConsumed?.();
    }
  }, [draftMessage, onDraftConsumed]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Mark messages as read when conversation is visible
  useEffect(() => {
    if (conversationId && messages.length > 0) {
      markRead();
    }
  }, [conversationId, messages.length, markRead]);

  // Listen for new_message self responses to capture conversation ID
  useEffect(() => {
    const unsub = wsClient.on("new_message", (data: any) => {
      if (data.self && data.message?.conversationId && !conversationId) {
        onConversationCreated?.(data.message.conversationId);
      }
    });
    return unsub;
  }, [conversationId, onConversationCreated]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    sendMessage(inputValue.trim());
    setInputValue("");
    inputRef.current?.focus();
  };

  const handleVoiceNoteSend = (url: string, duration: number) => {
    sendMessage("", { messageType: "voice_note", attachmentUrl: url, attachmentDuration: duration });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    sendTyping();
  };

  // Answer call handler — used by VoiceCallOverlay in case it ever shows an answer button
  const handleAnswerCall = () => {
    // This is a no-op. Incoming calls are handled by IncomingCallOverlay (admin)
    // or via the incomingCallSdp prop through ConversationPanel.
    console.warn("[WebRTC] handleAnswerCall called but incoming calls are handled by IncomingCallOverlay");
  };

  const isOwnMessage = (msg: any) => {
    if (role === "customer") return msg.senderRole === "customer";
    return msg.senderRole === "agent";
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      {showHeader && (
        <div className="p-3 bg-gradient-to-r from-[#D4AF37] to-[#B48F1B] text-black flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? "bg-emerald-600" : "bg-red-600"} animate-pulse`} />
            <span className="font-bold uppercase tracking-widest text-[9px]">
              {headerTitle}
            </span>
          </div>
          <button
            type="button"
            onClick={() => webrtc.startCall(conversationId)}
            className="p-1.5 hover:bg-black/10 rounded-full transition-colors"
            title="Start voice call"
          >
            <Phone size={14} />
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
        {messages.length === 0 && (
          <div className="flex justify-start">
            <div className="bg-[#1A1A1A] border border-gray-800 rounded-2xl p-3 text-xs text-gray-300 max-w-[80%]">
              Welcome! How can we assist you today?
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} isOwn={isOwnMessage(msg)} />
        ))}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="relative p-3 border-t border-gray-800 bg-[#0F0F0F]">
        <div className="flex items-center gap-2">
          {inputValue.trim() ? null : (
            <VoiceNoteRecorder onSend={handleVoiceNoteSend} />
          )}
          {role === "admin" && (
            <QuickReplies
              onSelect={(text) => setInputValue(text)}
              companionProfiles={companions.map(c => ({ id: String(c.id), name: c.name }))}
            />
          )}
          {role === "admin" && !inputValue.trim() && (
            <MediaUploadButton onSend={(url, mode, mediaType) => {
              sendMessage(mode, { messageType: mediaType, attachmentUrl: url });
            }} />
          )}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Type a message..."
            className="flex-1 bg-[#1A1A1A] border border-gray-800 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[#D4AF37] transition-colors"
          />
          {inputValue.trim() && (
            <button
              type="submit"
              title="Send message"
              className="p-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#B48F1B] transition-colors"
            >
              <Send size={16} />
            </button>
          )}
        </div>
      </form>

      {/* Voice call overlay */}
      <AnimatePresence>
        <VoiceCallOverlay
          callState={webrtc.callState}
          isMuted={webrtc.isMuted}
          callDuration={webrtc.callDuration}
          onAnswer={handleAnswerCall}
          onHangUp={webrtc.hangUp}
          onToggleMute={webrtc.toggleMute}
        />
      </AnimatePresence>
    </div>
  );
});
